/**
 * Job Schedule Generator Utilities
 * 
 * Generates schedules for different job types based on event data.
 * All times are calculated in the circuit's timezone and converted to UTC for storage.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Session } from '../api/eventsService';
import type { CreateEventJobRequest } from '../api/eventJobsService';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Generate weather message jobs based on session schedule
 * Uses the schedule patterns analyzed from legacy messages
 */
export function generateWeatherMessageJobs(
  sessions: Session[],
  circuitTimezone: string
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  
  for (const session of sessions) {
    const sessionCode = session.session_name;
    // Skip sessions without a name
    if (!sessionCode || !sessionCode.trim()) continue;
    
    // Skip sessions without valid start_time
    if (!session.start_time) continue;
    
    // Parse session start time (ISO string) and convert to circuit timezone
    const sessionStart = dayjs(session.start_time).tz(circuitTimezone);
    
    // Skip if start_time is invalid
    if (!sessionStart.isValid()) continue;
    
    // Unified schedule for all sessions: temp at -3h, temp at -1h, rain at -1h, temp at -15m
    
    // Temperature - 3 hours before
    jobs.push({
      job_type: 'weather_message',
      scheduled_at: sessionStart.subtract(3, 'hour').utc().toISOString(),
      job_config: {
        session_code: sessionCode.trim(),
        message_type: 'air_temperature',
      },
    });
    
    // Temperature - 1 hour before
    const temp1h = sessionStart.subtract(1, 'hour');
    jobs.push({
      job_type: 'weather_message',
      scheduled_at: temp1h.utc().toISOString(),
      job_config: {
        session_code: sessionCode.trim(),
        message_type: 'air_temperature',
      },
    });
    
    // Rain - 1 hour before (same time as temp 1h, but separate job)
    jobs.push({
      job_type: 'weather_message',
      scheduled_at: temp1h.utc().toISOString(),
      job_config: {
        session_code: sessionCode.trim(),
        message_type: 'rain_probability',
      },
    });
    
    // Temperature - 15 minutes before
    jobs.push({
      job_type: 'weather_message',
      scheduled_at: sessionStart.subtract(15, 'minute').utc().toISOString(),
      job_config: {
        session_code: sessionCode.trim(),
        message_type: 'air_temperature',
      },
    });
  }
  
  return jobs;
}

export interface GenerateDailyForecastJobsOptions {
  /** When true and lastSessionEnd provided, adds 00:00 daily forecast for each day during the hourly period */
  includeHourlyPeriodDays?: boolean;
  lastSessionEnd?: string; // ISO timestamp
}

/**
 * Generate daily forecast jobs
 * - Pre-event days (until day before first session): 00:00, 04:00, 08:00, 12:00, 16:00, 20:00
 * - When includeHourlyPeriodDays: adds 00:00 daily for each day during hourly period (day before first session through end of last session)
 */
export function generateDailyForecastJobs(
  startDate: string, // YYYY-MM-DD
  firstSessionStart: string, // ISO timestamp
  circuitTimezone: string,
  options?: GenerateDailyForecastJobsOptions
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  
  // Parse dates in circuit timezone
  const eventStart = dayjs.tz(startDate, circuitTimezone).startOf('day');
  const firstSession = dayjs(firstSessionStart).tz(circuitTimezone);
  const dayBeforeFirstSession = firstSession.subtract(1, 'day').startOf('day');
  
  // Generate jobs for each day from event start until (but not including) day before first session
  let currentDate = eventStart;
  
  while (currentDate.isBefore(dayBeforeFirstSession)) {
    // Generate jobs at 00:00, 04:00, 08:00, 12:00, 16:00, 20:00 in circuit timezone
    const times = [0, 4, 8, 12, 16, 20];
    
    for (const hour of times) {
      const scheduledTime = currentDate.hour(hour).minute(0).second(0);
      
      jobs.push({
        job_type: 'daily_forecast',
        scheduled_at: scheduledTime.utc().toISOString(),
      });
    }
    
    // Move to next day
    currentDate = currentDate.add(1, 'day');
  }
  
  // When both daily and hourly are enabled: add 00:00 daily for each day during the hourly period
  if (options?.includeHourlyPeriodDays && options.lastSessionEnd) {
    const lastSession = dayjs(options.lastSessionEnd).tz(circuitTimezone);
    let hourlyPeriodDate = dayBeforeFirstSession;
    
    while (hourlyPeriodDate.isBefore(lastSession) || hourlyPeriodDate.isSame(lastSession, 'day')) {
      jobs.push({
        job_type: 'daily_forecast',
        scheduled_at: hourlyPeriodDate.hour(0).minute(0).second(0).utc().toISOString(),
      });
      hourlyPeriodDate = hourlyPeriodDate.add(1, 'day');
    }
  }
  
  return jobs;
}

export interface GenerateHourlyForecastJobsOptions {
  /** Sessions to add extra hourly jobs 1 hour before each session start */
  sessions?: Session[];
}

/**
 * Generate hourly forecast jobs
 * From day before first session until end of race (last session end).
 * Schedule: 01:00 (hourly for 00:00), then 04:00, 08:00, 12:00, 16:00, 20:00 per day.
 * This allows 00:00 to be used for daily forecast while hourly runs at 01:00 for the 00:00 slot.
 * When sessions are provided, adds additional hourly jobs 1 hour before each session start.
 */
export function generateHourlyForecastJobs(
  firstSessionStart: string, // ISO timestamp
  lastSessionEnd: string, // ISO timestamp
  circuitTimezone: string,
  options?: GenerateHourlyForecastJobsOptions
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  const scheduledTimes = new Set<string>(); // For deduplication
  
  // Parse dates in circuit timezone
  const firstSession = dayjs(firstSessionStart).tz(circuitTimezone);
  const dayBeforeFirstSession = firstSession.subtract(1, 'day').startOf('day');
  const lastSession = dayjs(lastSessionEnd).tz(circuitTimezone);
  
  const sortedSessions = options?.sessions?.length
    ? [...options.sessions].sort(
        (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      )
    : [];

  const addJobIfValid = (time: dayjs.Dayjs) => {
    if (time.isAfter(lastSession)) return;
    const iso = time.utc().toISOString();
    if (!scheduledTimes.has(iso)) {
      scheduledTimes.add(iso);
      jobs.push({
        job_type: 'hourly_forecast',
        scheduled_at: iso,
      });
    }
  };

  let currentDate = dayBeforeFirstSession;

  while (currentDate.isBefore(lastSession) || currentDate.isSame(lastSession, 'day')) {
    for (const hour of HOURLY_SCHEDULE_HOURS) {
      const scheduledTime = currentDate.hour(hour).minute(0).second(0);
      if (scheduledTime.isAfter(lastSession)) continue;

      if (sortedSessions.length === 0) {
        addJobIfValid(scheduledTime);
        continue;
      }

      const containing = getSessionContainingTime(scheduledTime, sortedSessions, circuitTimezone);
      if (!containing) {
        addJobIfValid(scheduledTime);
        continue;
      }

      if (containing.isLast) continue; // Skip jobs during last session

      const sessionEnd = dayjs(containing.session.end_time).tz(circuitTimezone);
      const pushedTime = getNextHourlySlot(sessionEnd);
      addJobIfValid(pushedTime);
    }

    currentDate = currentDate.add(1, 'day');
  }

  // Add additional hourly jobs 1 hour before each session start
  if (sortedSessions.length > 0) {
    for (const session of sortedSessions) {
      if (!session.start_time) continue;
      const sessionStart = dayjs(session.start_time).tz(circuitTimezone);
      const oneHourBefore = sessionStart.subtract(1, 'hour');
      if (oneHourBefore.isBefore(dayBeforeFirstSession)) continue;
      if (oneHourBefore.isAfter(lastSession)) continue;

      const containing = getSessionContainingTime(oneHourBefore, sortedSessions, circuitTimezone);
      if (containing) {
        if (containing.isLast) continue;
        const sessionEnd = dayjs(containing.session.end_time).tz(circuitTimezone);
        const pushedTime = getNextHourlySlot(sessionEnd);
        addJobIfValid(pushedTime);
      } else {
        addJobIfValid(oneHourBefore);
      }
    }
  }
  
  // Sort jobs by scheduled_at for consistent ordering
  jobs.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  
  return jobs;
}

/**
 * Generate minutely forecast jobs.
 * For each session, schedules jobs from 30 minutes before session start through
 * session end, every 5 minutes. Callers should only use this for prod/demo
 * events because minutely forecasts are not available for legacy events.
 */
export function generateMinutelyForecastJobs(
  sessions: Session[],
  circuitTimezone: string
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  const scheduledTimes = new Set<string>();

  const sortedSessions = [...sessions].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  for (const session of sortedSessions) {
    if (!session.start_time || !session.end_time) continue;

    const sessionStart = dayjs(session.start_time).tz(circuitTimezone);
    const sessionEnd = dayjs(session.end_time).tz(circuitTimezone);
    if (!sessionStart.isValid() || !sessionEnd.isValid()) continue;

    let current = sessionStart.subtract(30, 'minute').second(0).millisecond(0);

    while (current.isBefore(sessionEnd) || current.isSame(sessionEnd)) {
      const iso = current.utc().toISOString();
      if (!scheduledTimes.has(iso)) {
        scheduledTimes.add(iso);
        jobs.push({
          job_type: 'minutely_forecast',
          scheduled_at: iso,
          job_config: {
            session_name: session.session_name,
            session_start_time: session.start_time,
            session_end_time: session.end_time,
          },
        });
      }

      current = current.add(5, 'minute');
    }
  }

  jobs.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  return jobs;
}

/**
 * Check if a time falls within any session
 */
function isWithinSession(
  time: dayjs.Dayjs,
  sessions: Session[],
  circuitTimezone: string
): boolean {
  for (const session of sessions) {
    const start = dayjs(session.start_time).tz(circuitTimezone);
    const end = dayjs(session.end_time).tz(circuitTimezone);
    if (time.isSameOrAfter(start) && time.isSameOrBefore(end)) {
      return true;
    }
  }
  return false;
}

/**
 * Find the session (if any) that contains the given time.
 * Returns { session, isLast } or null if not in any session.
 */
function getSessionContainingTime(
  time: dayjs.Dayjs,
  sortedSessions: Session[],
  circuitTimezone: string
): { session: Session; isLast: boolean } | null {
  for (let i = 0; i < sortedSessions.length; i++) {
    const session = sortedSessions[i];
    const start = dayjs(session.start_time).tz(circuitTimezone);
    const end = dayjs(session.end_time).tz(circuitTimezone);
    if (time.isSameOrAfter(start) && time.isSameOrBefore(end)) {
      return { session, isLast: i === sortedSessions.length - 1 };
    }
  }
  return null;
}

/** Hourly forecast schedule slots (hours) */
const HOURLY_SCHEDULE_HOURS = [1, 4, 8, 12, 16, 20];

/**
 * Get the next schedule slot (01:00, 04:00, 08:00, 12:00, 16:00, 20:00) strictly after the given time.
 */
function getNextHourlySlot(afterTime: dayjs.Dayjs): dayjs.Dayjs {
  const dayStart = afterTime.startOf('day');
  for (const hour of HOURLY_SCHEDULE_HOURS) {
    const slot = dayStart.hour(hour).minute(0).second(0);
    if (slot.isAfter(afterTime)) return slot;
  }
  // Next day, first slot
  return dayStart.add(1, 'day').hour(HOURLY_SCHEDULE_HOURS[0]).minute(0).second(0);
}

export interface GeneratePipelineStatusCheckJobsOptions {
  /** When provided, jobs start from this time instead of event start. ISO timestamp (UTC). */
  monitorStartTime?: string;
}

/**
 * Generate pipeline status check jobs (stations + radar).
 * Every 15 minutes during regular event times, every 5 minutes during sessions.
 * No jobs after last session end.
 */
export function generatePipelineStatusCheckJobs(
  startDate: string, // YYYY-MM-DD
  sessions: Session[],
  circuitTimezone: string,
  options?: GeneratePipelineStatusCheckJobsOptions
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  if (sessions.length === 0) return jobs;

  const sortedSessions = [...sessions].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
  const lastSession = sortedSessions[sortedSessions.length - 1];
  if (!lastSession?.end_time) return jobs;

  const eventStart = dayjs.tz(startDate, circuitTimezone).startOf('day');
  const lastSessionEnd = dayjs(lastSession.end_time).tz(circuitTimezone);

  let current: dayjs.Dayjs;
  if (options?.monitorStartTime) {
    const rawStart = dayjs(options.monitorStartTime).tz(circuitTimezone);
    // Round up to next 5-minute boundary only when not already on a boundary
    const totalMins = rawStart.hour() * 60 + rawStart.minute();
    const roundedMins =
      totalMins % 5 === 0 ? totalMins : Math.ceil((totalMins + 1) / 5) * 5;
    const startHour = Math.floor(roundedMins / 60) % 24;
    const startMin = roundedMins % 60;
    current =
      roundedMins >= 24 * 60
        ? rawStart.add(1, 'day').startOf('day')
        : rawStart.hour(startHour).minute(startMin).second(0).millisecond(0);
    if (current.isBefore(eventStart)) current = eventStart;
  } else {
    // Default: third day of event at 16:00
    current = eventStart.add(2, 'day').hour(16).minute(0).second(0).millisecond(0);
    if (current.isAfter(lastSessionEnd)) current = eventStart;
  }
  while (current.isBefore(lastSessionEnd) || current.isSame(lastSessionEnd)) {
    const isInSession = isWithinSession(current, sessions, circuitTimezone);
    const minute = current.minute();

    const shouldAdd = isInSession
      ? true // Every 5 min during sessions (:00, :05, :10, ...)
      : minute % 15 === 0; // Every 15 minutes during regular times

    if (shouldAdd && current.isSameOrBefore(lastSessionEnd)) {
      jobs.push({
        job_type: 'stations_status_check',
        scheduled_at: current.utc().toISOString(),
      });
      jobs.push({
        job_type: 'radar_status_check',
        scheduled_at: current.utc().toISOString(),
      });
    }

    current = current.add(5, 'minute');
  }

  return jobs;
}

/**
 * Generate backup jobs
 * One job per day at end of day (23:59:59) in circuit timezone
 */
export function generateBackupJobs(
  startDate: string, // YYYY-MM-DD
  endDate: string, // YYYY-MM-DD
  circuitTimezone: string
): CreateEventJobRequest[] {
  const jobs: CreateEventJobRequest[] = [];
  
  // Parse dates in circuit timezone
  const eventStart = dayjs.tz(startDate, circuitTimezone).startOf('day');
  const eventEnd = dayjs.tz(endDate, circuitTimezone).endOf('day');
  
  // Generate one job per day at end of day (23:59:59)
  let currentDate = eventStart;
  
  while (currentDate.isBefore(eventEnd) || currentDate.isSame(eventEnd, 'day')) {
    // Use explicit 23:59:59 to avoid dayjs endOf('day') timezone bugs (see dayjs#1573)
    const endOfDay = currentDate.hour(23).minute(59).second(59).millisecond(999);
    
    jobs.push({
      job_type: 'backup',
      scheduled_at: endOfDay.utc().toISOString(),
    });
    
    // Move to next day
    currentDate = currentDate.add(1, 'day');
  }
  
  return jobs;
}

/**
 * Generate archive observations job.
 * One job per event, scheduled at the last day of the event at 23:59:59 circuit time
 * (same timing as the last backup job).
 */
export function generateArchiveObservationsJobs(
  endDate: string, // YYYY-MM-DD
  circuitTimezone: string
): CreateEventJobRequest[] {
  const archiveTime = dayjs.tz(endDate, circuitTimezone)
    .hour(23).minute(59).second(59).millisecond(999);

  return [{
    job_type: 'archive_observations',
    scheduled_at: archiveTime.utc().toISOString(),
  }];
}

/**
 * Get job counts summary for display
 */
export interface JobCounts {
  weatherMessages: number;
  dailyForecast: number;
  hourlyForecast: number;
  minutelyForecast: number;
  backup: number;
  archiveObservations: number;
  stationsStatusCheck: number;
  radarStatusCheck: number;
  total: number;
}

export function getJobCounts(
  weatherMessageJobs: CreateEventJobRequest[],
  dailyForecastJobs: CreateEventJobRequest[],
  hourlyForecastJobs: CreateEventJobRequest[],
  minutelyForecastJobs: CreateEventJobRequest[],
  backupJobs: CreateEventJobRequest[],
  pipelineStatusCheckJobs: CreateEventJobRequest[] = [],
  archiveObservationsJobs: CreateEventJobRequest[] = []
): JobCounts {
  const stationsCount = pipelineStatusCheckJobs.filter(j => j.job_type === 'stations_status_check').length;
  const radarCount = pipelineStatusCheckJobs.filter(j => j.job_type === 'radar_status_check').length;
  return {
    weatherMessages: weatherMessageJobs.length,
    dailyForecast: dailyForecastJobs.length,
    hourlyForecast: hourlyForecastJobs.length,
    minutelyForecast: minutelyForecastJobs.length,
    backup: backupJobs.length,
    archiveObservations: archiveObservationsJobs.length,
    stationsStatusCheck: stationsCount,
    radarStatusCheck: radarCount,
    total: weatherMessageJobs.length + dailyForecastJobs.length + hourlyForecastJobs.length + minutelyForecastJobs.length + backupJobs.length + pipelineStatusCheckJobs.length + archiveObservationsJobs.length,
  };
}

